import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Api from "../Api";
import { BASE_API_URL } from "../const";

const EMPTY_FILTERS = {
  genres: [],
  countries: [],
  bundle: [],
  genreBlackList: null,
  countryBlackList: null,
  bundleBlackList: null,
  year: null,
  last_show: null,
  format: "",
};

const COMPACT_CONTROL_SX = {
  "& .MuiInputBase-root": { fontSize: "0.875rem" },
  "& .MuiInputLabel-root": { fontSize: "0.875rem" },
};

const COMPACT_MENU_PROPS = {
  PaperProps: {
    sx: { "& .MuiMenuItem-root": { fontSize: "0.875rem" } },
  },
};

const emptyForm = () => ({ id: null, name: "", filters: { ...EMPTY_FILTERS } });

const normalizeFilters = (filters = {}) => ({
  genres: Array.isArray(filters.genres) ? filters.genres.map(String) : [],
  countries: Array.isArray(filters.countries)
    ? filters.countries.map(String)
    : [],
  bundle: Array.isArray(filters.bundle) ? filters.bundle.map(String) : [],
  genreBlackList: filters.genreBlackList ?? null,
  countryBlackList: filters.countryBlackList ?? null,
  bundleBlackList: filters.bundleBlackList ?? null,
  year: filters.year == null ? null : String(filters.year),
  last_show: filters.last_show == null ? null : String(filters.last_show),
  format: filters.format == null ? "" : String(filters.format),
});

const serializeFilters = (filters) => {
  const result = {};
  ["genres", "countries", "bundle"].forEach((key) => {
    if (filters[key].length) result[key] = filters[key].map(String);
  });
  ["genreBlackList", "countryBlackList", "bundleBlackList"].forEach(
    (key) => {
      if (filters[key] !== null) result[key] = filters[key];
    }
  );
  ["year", "last_show"].forEach((key) => {
    if (filters[key] !== null && filters[key] !== "") {
      result[key] = String(filters[key]);
    }
  });
  if (filters.format !== "" && filters.format !== null) {
    result.format = String(filters.format);
  }
  return result;
};

const getApiError = (error, fallback) => {
  const apiError = error?.response?.data?.error;
  const message =
    apiError?.message || error?.response?.data?.message || error?.message || fallback;
  return apiError?.code ? `${message} (код ${apiError.code})` : message;
};

const PresetManager = ({
  open,
  onClose,
  responseFilter = {},
  onPresetsChange,
}) => {
  const [presets, setPresets] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const dictionaries = useMemo(
    () => ({
      genres: responseFilter.genre || [],
      countries: responseFilter.country || [],
      bundle: (responseFilter.bundle || []).filter(
        (item) => !["7", "8"].includes(String(item.id))
      ),
      year: responseFilter.year || [],
      last_show: responseFilter.last_show || [],
      format: responseFilter.format || [],
    }),
    [responseFilter]
  );

  const loadPresets = async () => {
    setLoading(true);
    try {
      const response = await Api.get(`${BASE_API_URL}/api/movie-preset/list`);
      const list = Array.isArray(response.data?.data) ? response.data.data : [];
      const sortedList = [...list].sort((a, b) => Number(a.id) - Number(b.id));
      setPresets(sortedList);
      onPresetsChange?.(sortedList);
    } catch (error) {
      toast.error(getApiError(error, "Не удалось загрузить список пресетов"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadPresets();
      setForm(emptyForm());
    }
  }, [open]);

  const selectPreset = (preset) => {
    setForm({
      id: Number(preset.id),
      name: preset.name || "",
      filters: normalizeFilters(preset.filters),
    });
  };

  const setFilter = (key, value) => {
    setForm((current) => ({
      ...current,
      filters: { ...current.filters, [key]: value },
    }));
  };

  const validate = () => {
    const name = form.name.trim();
    if (!name) {
      toast.warning("Название пресета обязательно");
      return null;
    }
    if (name.length > 255) {
      toast.warning("Название пресета не должно превышать 255 символов");
      return null;
    }

    const invalidBundles = form.filters.bundle.filter((id) =>
      ["7", "8"].includes(String(id))
    );
    if (invalidBundles.length) {
      toast.warning("Пакеты 7 и 8 нельзя сохранять в пресете");
      return null;
    }

    const invalidValue = ["genres", "countries", "bundle"].find((key) => {
      const available = new Set(
        dictionaries[key].map((item) => String(item.id))
      );
      return form.filters[key].some((id) => !available.has(String(id)));
    });
    const invalidSingleValue = ["year", "last_show", "format"].find((key) => {
      const value = form.filters[key];
      return (
        value !== null &&
        value !== "" &&
        !dictionaries[key].some((item) => String(item.id) === String(value))
      );
    });
    if (invalidValue || invalidSingleValue) {
      toast.warning("Удалите или замените недоступные значения фильтров");
      return null;
    }

    return { name, filters: serializeFilters(form.filters) };
  };

  const createPreset = async () => {
    const payload = validate();
    if (!payload || saving) return;
    setSaving(true);
    try {
      const response = await Api.post(
        `${BASE_API_URL}/api/movie-preset/add`,
        payload
      );
      const created = response.data?.data;
      if (!created) throw new Error("Сервер не вернул созданный пресет");
      setPresets((current) =>
        [...current, created].sort((a, b) => Number(a.id) - Number(b.id))
      );
      onPresetsChange?.([...presets, created]);
      selectPreset(created);
      await loadPresets();
      toast.success(`Пресет «${created.name}» создан`);
    } catch (error) {
      toast.error(getApiError(error, "Не удалось создать пресет"));
    } finally {
      setSaving(false);
    }
  };

  const updatePreset = async () => {
    if (!form.id) {
      toast.warning("Выберите пресет для редактирования");
      return;
    }
    const payload = validate();
    if (!payload || saving) return;
    setSaving(true);
    try {
      const response = await Api.post(
        `${BASE_API_URL}/api/movie-preset/update`,
        { id: form.id, ...payload }
      );
      const updated = response.data?.data;
      if (!updated) throw new Error("Сервер не вернул обновлённый пресет");
      setPresets((current) =>
        current
          .map((preset) =>
            Number(preset.id) === Number(updated.id) ? updated : preset
          )
          .sort((a, b) => Number(a.id) - Number(b.id))
      );
      onPresetsChange?.(
        presets.map((preset) =>
          Number(preset.id) === Number(updated.id) ? updated : preset
        )
      );
      selectPreset(updated);
      await loadPresets();
      toast.success(`Пресет «${updated.name}» отредактирован`);
    } catch (error) {
      toast.error(getApiError(error, "Не удалось отредактировать пресет"));
      if ([404, 490].includes(error?.response?.status)) loadPresets();
    } finally {
      setSaving(false);
    }
  };

  const removePreset = async () => {
    if (!form.id || deleting) return;
    if (
      !window.confirm(
        `Удалить пресет «${form.name}» из общего списка пользователей?`
      )
    ) {
      return;
    }

    setDeleting(true);
    try {
      const response = await Api.post(
        `${BASE_API_URL}/api/movie-preset/remove`,
        { id: form.id }
      );
      if (response.status !== 200 || response.data?.success !== true) {
        throw new Error("Сервер не подтвердил удаление пресета");
      }
      const deletedName = form.name;
      setPresets((current) =>
        current.filter((preset) => Number(preset.id) !== Number(form.id))
      );
      onPresetsChange?.(
        presets.filter((preset) => Number(preset.id) !== Number(form.id))
      );
      setForm(emptyForm());
      await loadPresets();
      toast.success(`Пресет «${deletedName}» удалён`);
    } catch (error) {
      toast.error(getApiError(error, "Не удалось удалить пресет"));
      if ([404, 490].includes(error?.response?.status)) loadPresets();
    } finally {
      setDeleting(false);
    }
  };

  const unavailableValues = (key) => {
    const available = new Set(dictionaries[key].map((item) => String(item.id)));
    const selected = Array.isArray(form.filters[key]) ? form.filters[key] : [];
    return selected.filter((id) => !available.has(String(id)));
  };

  const multiSelect = (key, label) => (
    <FormControl fullWidth size="small" sx={COMPACT_CONTROL_SX}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        MenuProps={COMPACT_MENU_PROPS}
        label={label}
        value={form.filters[key]}
        onChange={(event) => setFilter(key, event.target.value.map(String))}
        renderValue={(selected) => selected.map(String).join(", ")}
      >
        {dictionaries[key].map((item) => (
          <MenuItem
            key={item.id}
            value={String(item.id)}
            sx={{
              fontWeight: form.filters[key].includes(String(item.id))
                ? "bold"
                : "normal",
            }}
          >
            {item.name}
          </MenuItem>
        ))}
        {unavailableValues(key).map((id) => (
          <MenuItem
            key={`unavailable-${id}`}
            value={String(id)}
            sx={{ fontWeight: "bold" }}
          >
            Недоступное значение, ID {id}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const singleSelect = (key, label, emptyValue = null) => (
    <FormControl fullWidth size="small" sx={COMPACT_CONTROL_SX}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        MenuProps={COMPACT_MENU_PROPS}
        value={form.filters[key] ?? ""}
        onChange={(event) =>
          setFilter(key, event.target.value === "" ? emptyValue : String(event.target.value))
        }
      >
        <MenuItem value="">Любой</MenuItem>
        {dictionaries[key].map((item) => (
          <MenuItem key={item.id} value={String(item.id)}>
            {item.name}
          </MenuItem>
        ))}
        {form.filters[key] !== null &&
          form.filters[key] !== "" &&
          !dictionaries[key].some(
            (item) => String(item.id) === String(form.filters[key])
          ) && (
            <MenuItem value={String(form.filters[key])}>
              Недоступное значение, ID {form.filters[key]}
            </MenuItem>
          )}
      </Select>
    </FormControl>
  );

  return (
    <Dialog
      maxWidth="lg"
      fullWidth
      open={open}
      onClose={saving || deleting ? undefined : onClose}
      PaperProps={{
        sx: {
          width: "800px",
          maxWidth: "calc(100% - 64px)",
          minHeight: "720px",
        },
      }}
    >
      <DialogTitle>Управление пресетами</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", gap: 4, pt: 1 }}>
          <Box sx={{ width: "38%" }}>
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <Button variant="contained" onClick={() => setForm(emptyForm())}>
                Новый пресет
              </Button>
            </Box>
            <TableContainer sx={{ maxHeight: 560 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Список пресетов</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : presets.length ? (
                    presets.map((preset) => (
                      <TableRow
                        hover
                        selected={Number(form.id) === Number(preset.id)}
                        key={preset.id}
                        onClick={() => selectPreset(preset)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>{preset.id}</TableCell>
                        <TableCell>{preset.name}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        Пресетов пока нет
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box
            sx={{
              width: "62%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography variant="h6" align="center">
              {form.id
                ? `Редактирование пресета «${form.name}»`
                : "Новый пресет"}
            </Typography>
            <TextField
              size="small"
              label="Название"
              value={form.name}
              inputProps={{ maxLength: 255 }}
              onChange={(event) =>
                setForm((current) => ({ ...current, name: event.target.value }))
              }
              helperText={`${form.name.trim().length}/255`}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ flex: 1 }}>{multiSelect("genres", "Жанры")}</Box>
              <FormControlLabel
                sx={{
                  minWidth: 165,
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.875rem" },
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={form.filters.genreBlackList === true}
                    onChange={(event) =>
                      setFilter("genreBlackList", event.target.checked)
                    }
                  />
                }
                label="Исключить жанры"
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                {multiSelect("countries", "Страны")}
              </Box>
              <FormControlLabel
                sx={{
                  minWidth: 165,
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.875rem" },
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={form.filters.countryBlackList === true}
                    onChange={(event) =>
                      setFilter("countryBlackList", event.target.checked)
                    }
                  />
                }
                label="Исключить страны"
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ flex: 1 }}>{multiSelect("bundle", "Пакеты")}</Box>
              <FormControlLabel
                sx={{
                  minWidth: 165,
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.875rem" },
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={form.filters.bundleBlackList === true}
                    onChange={(event) =>
                      setFilter("bundleBlackList", event.target.checked)
                    }
                  />
                }
                label="Исключить пакеты"
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 2,
              }}
            >
              {singleSelect("year", "Год")}
              {singleSelect("last_show", "Эфир")}
              {singleSelect("format", "Формат", "")}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Изменения пресета затронут общий список всех пользователей.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        {form.id ? (
          <>
            <Button color="error" onClick={removePreset} disabled={saving || deleting}>
              {deleting ? <CircularProgress size={20} /> : "Удалить"}
            </Button>
            <Button variant="contained" onClick={updatePreset} disabled={saving || deleting}>
              {saving ? <CircularProgress size={20} /> : "Сохранить"}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={createPreset} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Создать"}
          </Button>
        )}
        <Button onClick={onClose} disabled={saving || deleting}>
          Закрыть
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PresetManager;
